"use client";

import React, { useState, useEffect } from "react";
import BookingTable from "../common/Table";

import {
  useUpdateVehicleNameStatus,
  useAddVehicleName,
  getVehicleNameTypeList,
  getVehicleTypeList,
  getVehicleAmenitiesList,
  useUpdateVehicleNameData,
  useGetCompanyDetail // Add this import
} from "@/hooks/useCommon";

import {
  X,
  Trash2,
  Edit,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
  Plus,
  Search,
} from "lucide-react";
import DynamicForm from "./Popup";
import Button from "./Button";
import { toast } from "react-toastify";
import { useAuth } from "@/store/auth";


interface TableData {
  vehicle_type_id: number;
  sno: number;
  order: string;
  image: string;
  vehicleType: string;
  seatingCapacity: string;
  luggage: string;
  luggage_capacity: string;
  small_suitcase: string;
  person_capacity: string;
  no_of_doors: number;
  no_of_airbags: number;
  vehicleImage: string;
  leftVehicleImage: string;
  rightVehicleImage: string;
  frontVehicleImage: string;
  backVehicleImage: string;
  interiorVehicleImage: string;
  createdBy: string;
  status: string;
  name: string;
  amenities_id: string;
  amenties: string;
  id: string;
  company_id?: number;
}

interface CompanyData {
  companyData: {
    id: number;
    company_name: string;
    brand_name: string | null;
    // ... other company fields
  };
  gstData: any[];
}

const ManageVehicleName = () => {
  const [vehicleType, setVehicleType] = useState("");
  const [companyId, setCompanyId] = useState<string>(""); // Add company filter state
  const [activeStatus, setActiveStatus] = useState(true);
  const [inactiveStatus, setInactiveStatus] = useState(false);
  const [currentRow, setCurrentRow] = useState<TableData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});
  const [uploadedImageUrls, setUploadedImageUrls] = useState<
    Record<string, string>
  >({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { data: vehicleDetail, refetch } = getVehicleNameTypeList();
  const [rawVehicleData, setRawVehicleData] = useState<any[]>([]);
  const [filteredVehicleData, setFilteredVehicleData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
  const { mutate: updateStatusMutate } = useUpdateVehicleNameStatus();
  const { mutate: addVehicleTypeMutate } = useAddVehicleName();
  const { mutate: updateVehicleTypeMutate } = useUpdateVehicleNameData();
  const [vehicleName, setVehicleName] = useState("");
  const { data: vehicleTypeList, refetch: vListRefech } = getVehicleTypeList();
  const { data: vehicleAmenties, refetch: refetchAmenties } =
    getVehicleAmenitiesList();

  // Add company data hook
  const { data: companyData, isLoading: companyLoading } = useGetCompanyDetail();

  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
  const user = useAuth(); 
  const userId = user?.user?.id;
  
  useEffect(() => {
    refetch();
    vListRefech();
    refetchAmenties();
  }, []);
  
  useEffect(() => {
    if (Array.isArray(vehicleDetail)) {
      setRawVehicleData(vehicleDetail);
      setFilteredVehicleData(vehicleDetail);
    }
  }, [vehicleDetail]);

  // Format company options from API response
  const companyOptions = React.useMemo(() => {
    if (!companyData || !Array.isArray(companyData)) {
      return [];
    }
    
    return companyData.map((item: CompanyData) => ({
      value: item.companyData.id.toString(),
      label: item.companyData.company_name || `Company ${item.companyData.id}`
    }));
  }, [companyData]);

  const vechicleData = Array.isArray(filteredVehicleData)
    ? filteredVehicleData.map((item: any, index: number) => ({
        sno: index + 1,
        order: item.display_order,
        amenities: item.amenities,
        id: item.id,
        vehicle_image: item.image,
        leftVehicleImage: item.left_image,
        rightVehicleImage: item.right_image,
        frontVehicleImage: item.front_image,
        backVehicleImage: item.back_image,
        interiorVehicleImage: item.interior_image,
        vehicleType: item.vehicle_type,
        no_of_doors: item.no_of_doors,
        no_of_airbags: item.no_of_airbags,
        vehicle_type: item.vehicle_type_name,
        status: item.status,
        created_name: item.created_name,
        name: item.name,
        aircondition: item.aircondition,
        email: item.email,
        mobile: item.mobile,
        luggage_capacity: item.luggage_capacity,
        person_capacity: item.person_capacity,
        small_suitcase: item.small_suitcase,
        vehicle_type_id: item.vehicle_type_id,
        vehicle_type_name: item.vehicle_type_name,
        amenties: item.amenties,
        company_id: item.company_id,
        action: "View",
      }))
    : [];

  const handleSearch = () => {
    const filtered = rawVehicleData.filter((item) => {
      const matchVehicleType = vehicleType
        ? item.vehicle_type_id == vehicleType
        : true;
      
      const matchCompany = companyId ? item.company_id == companyId : true; // Add company filter

      const matchStatus =
        activeStatus && inactiveStatus
          ? true
          : activeStatus
          ? item.status == 1
          : inactiveStatus
          ? item.status == 0
          : true;
      const matchName = vehicleName ? item.id == vehicleName : true;

      return matchVehicleType && matchCompany && matchStatus && matchName;
    });

    setFilteredVehicleData(filtered);
  };


  const handleFormSubmit = (formData: Record<string, any>, type: string) => {
    const {
      vehicleType,
      status,
      vehicleImage,
      vehicleName,
      luggage,
      seatingCapacity,
      no_of_doors,
      no_of_airbags,
      smallSuitcase,
      amenties,
      leftVehicleImage,
      rightVehicleImage,
      frontVehicleImage,
      backVehicleImage,
      interiorVehicleImage,
      company_id, // Get company_id from form
    } = formData;

    const mainImageFile = vehicleImage instanceof FileList ? vehicleImage[0] : vehicleImage;
    const leftImageFile = leftVehicleImage instanceof FileList ? leftVehicleImage[0] : leftVehicleImage;
    const rightImageFile = rightVehicleImage instanceof FileList ? rightVehicleImage[0] : rightVehicleImage;
    const frontImageFile = frontVehicleImage instanceof FileList ? frontVehicleImage[0] : frontVehicleImage;
    const backImageFile = backVehicleImage instanceof FileList ? backVehicleImage[0] : backVehicleImage;
    const interiorImageFile = interiorVehicleImage instanceof FileList ? interiorVehicleImage[0] : interiorVehicleImage;

    const matchedVehicle = vehicleTypeList.find(
      (item: any) => item.id == vehicleType
    );

    console.log("matched vehicle", matchedVehicle);

    const val = matchedVehicle?.id ?? "";

    const amenityString = Array.isArray(amenties)
      ? amenties.map((item) => item.label).join(",")
      : "";
    const amenityIds = Array.isArray(amenties)
      ? amenties.map((item) => item.value).join(",")
      : "";

    // Create payload with all images
    const payload: any = {
      file: mainImageFile,
      folder: vehicleName,
      category_id: vehicleType,
      status,
      vehicle_type_id: val,
      name: vehicleName,
      person_capacity: seatingCapacity,
      luggage_capacity: luggage,
      small_suitcase: smallSuitcase,
      vehicle_type_name: matchedVehicle?.vehicle_type,
      amenities: amenityString,
      amenities_id: amenityIds,
      no_of_doors: no_of_doors,
      no_of_airbags: no_of_airbags,
      company_id: company_id ? parseInt(company_id) : undefined, // Add company_id
      user_id: userId, // Add user_id
    };

    console.log("Payload:", payload);

    // Add the ID for update operation
    if (type === "update") {
      payload.id = currentRow?.id;
    }

    // Add image files to payload
    if (leftImageFile) {
      payload.left = leftImageFile;
    }
    if (rightImageFile) {
      payload.right = rightImageFile;
    }
    if (frontImageFile) {
      payload.front = frontImageFile;
    }
    if (backImageFile) {
      payload.back = backImageFile;
    }
    if (interiorImageFile) {
      payload.interior = interiorImageFile;
    }

    if (type == "update") {
      updateVehicleTypeMutate(
        payload,
        {
          onSuccess: (res) => {
            toast.success("Vehicle name updated successfully");
            refetch();
            setIsModalOpen(false);
            setCurrentRow(null);
          },
          onError: () => {
            toast.error("Failed to update vehicle name");
          },
        }
      );
    } else {
      addVehicleTypeMutate(
        payload,
        {
          onSuccess: (res) => {
            toast.success("Vehicle name added successfully");
            refetch();
            setIsAddModalOpen(false);
            setCurrentRow(null);
          },
          onError: () => {
            toast.error("Failed to add vehicle name");
          },
        }
      );
    }
  };

  const onStatusChanged = (status_id: number, id?: any) => {
    let type_ids = [id];
    if (!id) {
      if (selectedRowIds.length === 0) {
        toast.error("No rows selected");
        return;
      }

      const selectedRoutes = filteredVehicleData.filter((row) =>
        selectedRowIds.includes(row.id)
      );
      type_ids = selectedRoutes.map((row) => row.id);
    }
    updateStatusMutate(
      {
        status: status_id,
        id: type_ids,
      },
      {
        onSuccess: (data) => {
          if (data.status == "success") {
            status_id == 2 ? "Data deleted successully" : data?.message;
          }
          refetch();
          setSelectedRowIds([]);
        },
        onError: (error) => {
          toast.error("Something want wrong");
        },
      }
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allIds = filteredVehicleData.map((row) => row.id);
      setSelectedRowIds(allIds);
    } else {
      setSelectedRowIds([]);
    }
  };

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedRowIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const allRowsSelected =
    filteredVehicleData?.length > 0 &&
    selectedRowIds?.length === filteredVehicleData?.length;

  const getImageUrl = (url: any) => {
    const rawFilePath = url || "";
    const fileParts = rawFilePath.split("/");
    const folderPath = fileParts.slice(0, -1).join("/");
    const fileName = fileParts[fileParts.length - 1];
    const imageUrl = `${profilePath}${folderPath}/${encodeURIComponent(
      fileName
    )}`;

   
    return imageUrl;
  };

  const columns = [
    {
      key: "checkbox",
      header: (
        <input
          type="checkbox"
          onChange={(e) => handleSelectAll(e.target.checked)}
          checked={allRowsSelected}
        />
      ),
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRowIds.includes(row.id)}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    {
      key: "left_image",
      header: "Left Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.leftVehicleImage);
       
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-24 h-20 object-contain rounded bg-white"
          />
        );
      },
    },
    {
      key: "right_image",
      header: "Right Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.rightVehicleImage);
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-24 h-20 object-contain rounded bg-white"
          />
        );
      },
    },
    {
      key: "front_image",
      header: "Front Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.frontVehicleImage);
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-24 h-20 object-contain rounded bg-white"
          />
        );
      },
    },
    {
      key: "back_image",
      header: "Back Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.backVehicleImage);
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-24 h-20 object-contain rounded bg-white"
          />
        );
      },
    },
    {
      key: "interior_image",
      header: "Interior Image",
      sortable: true,
      render: (_: any, row: any) => {
        const imageUrl = getImageUrl(row?.interiorVehicleImage);
        return (
          <img
            src={imageUrl || "/no-preview.png"}
            alt="Document Preview"
            className="w-24 h-20 object-contain rounded bg-white"
          />
        );
      },
    },
    { key: "name", header: "Vehicle Name", sortable: true },
    { key: "vehicle_type", header: "Vehicle Type", sortable: true },
    { key: "amenties", header: "amenties", sortable: true },
    { key: "id", header: "id", sortable: true, hidden: true },
    { key: "person_capacity", header: "Seating Capacity", sortable: true },
    { key: "luggage_capacity", header: "Luggae Capacity", sortable: true },
    { key: "small_suitcase", header: "Small Suitcase", sortable: true },
    { key: "no_of_airbags", header: "No. Of Airbags", sortable: true },
    { key: "no_of_doors", header: "No. Of Doors", sortable: true },
    {
      key: "created_name",
      header: "Created By",
      sortable: true,
      render: (_: string, row: any) => (
        <div className="space-y-1">
          <div> {row?.created_name}</div>
          <div> {row?.mobile}</div>
          <div> {row?.email}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (value: number | string) => {
        const isOnline = Number(value) == 1;
        return (
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                isOnline ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm font-medium">
              {isOnline ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentRow(row);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => onStatusChanged(2)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];
  
  const vehicleOptions = Array.isArray(vehicleTypeList)
    ? vehicleTypeList.map((item: any) => ({
        label: item.vehicle_type,
        value: item.id,
      }))
    : [];

  const amentiesListOptions = Array.isArray(vehicleAmenties)
    ? vehicleAmenties.map((item: any) => ({
        label: item.amenities_name,
        value: item.id,
      }))
    : [];

  const vehicleNameOptions = Array.isArray(vehicleDetail)
    ? vehicleDetail.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
    : [];

  // Updated form fields with company dropdown
  const formFields: any = [
    {
      id: "company_id",
      label: "Company",
      type: "select",
      placeholder: "Select Company",
      required: true,
      defaultValue: currentRow?.company_id?.toString() || "",
      options: companyOptions,
    },
    {
      id: "vehicleType",
      label: "Vehicle Type",
      type: "select",
      placeholder: "Select Vehicle Type",
      required: true,
      defaultValue: currentRow?.vehicle_type_id || "",
      options: vehicleOptions,
    },
    {
      id: "vehicleName",
      label: "Vehicle Name",
      type: "text",
      placeholder: "Enter Vehicle Name",
      required: true,
      defaultValue: currentRow?.name || "",
    },
    {
      id: "amenties",
      label: "Amenities Type",
      type: "multiselect", // ✅ custom type
      placeholder: "Select Amenities",
      required: true,
      defaultValue:
        typeof currentRow?.amenties === "string"
          ? currentRow.amenties.split(",").map((label: string) => {
              const trimmed = label.trim();
              return {
                label: trimmed,
                value: trimmed,
              };
            })
          : currentRow?.amenties || [],
      options: amentiesListOptions,
    },
    {
      id: "seatingCapacity",
      label: "Seating Capacity",
      type: "number",
      placeholder: "Enter Seating Capacity",
      required: true,
      defaultValue: currentRow?.person_capacity || "",
    },

    {
      id: "luggage",
      label: "Luggage Capacity",
      type: "number",
      placeholder: "Enter Luggage Capacity",
      required: true,
      defaultValue: currentRow?.luggage_capacity.toString() || "",
    },
    {
      id: "smallSuitcase",
      label: "Small Suitcase",
      type: "number",
      placeholder: "Enter Small Suitcase Capacity",
      required: true,
      defaultValue: currentRow?.small_suitcase || "",
      options: vehicleOptions,
    },

    {
      id: "no_of_doors",
      label: "No. Of Doors",
      type: "number",
      placeholder: "Enter No. of Doors",
      required: true,
      defaultValue: currentRow?.no_of_doors || "",
    },
     {
      id: "no_of_airbags",
      label: "No. Of Airbags",
      type: "number",
      placeholder: "Enter No. of Airbags",
      defaultValue: currentRow?.no_of_airbags || "",
    },
  

    {
      id: "leftVehicleImage",
      label: "Leftside Vehicle Image",
      type: "file",
      placeholder: "Upload Vehicle Image",
      required: true,
      defaultValue: currentRow?.leftVehicleImage || "",
      previewUrl: currentRow?.leftVehicleImage ? getImageUrl(currentRow.leftVehicleImage) : "",
    },
    {
      id: "rightVehicleImage",
      label: "Rightside Vehicle Image",
      type: "file",
      placeholder: "Upload Vehicle Image",
      required: true,
      defaultValue: currentRow?.rightVehicleImage || "",
      previewUrl: currentRow?.rightVehicleImage ? getImageUrl(currentRow.rightVehicleImage) : "",
    },
    {
      id: "frontVehicleImage",
      label: "FrontSide Vehicle Image",
      type: "file",
      placeholder: "Upload front Vehicle Image",
      required: true,
      defaultValue: currentRow?.frontVehicleImage || "",
      previewUrl: currentRow?.frontVehicleImage ? getImageUrl(currentRow.frontVehicleImage) : "",
    },
    {
      id: "backVehicleImage",
      label: "Back Vehicle Image",
      type: "file",
      placeholder: "Upload Back Vehicle Image",
      required: true,
      defaultValue: currentRow?.backVehicleImage || "",
       previewUrl: currentRow?.backVehicleImage ? getImageUrl(currentRow.backVehicleImage) : "",
    },
    {
      id: "interiorVehicleImage",
      label: "Interior Vehicle Image",
      type: "file",
      placeholder: "Upload Interior Vehicle Image",
      required: true,
      defaultValue: currentRow?.interiorVehicleImage || "",
      previewUrl: currentRow?.interiorVehicleImage ? getImageUrl(currentRow.interiorVehicleImage) : "",
    },
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "Select Status",
      required: true,
      defaultValue: currentRow?.status.toString() || "1",
      options: [
        { value: "1", label: "Active" },
        { value: "0", label: "In-Active" },
      ],
    },
  ];

  const handleClear = () => {
    setVehicleType("");
    setVehicleName("");
    setCompanyId(""); // Clear company filter
    setActiveStatus(true);
    setInactiveStatus(false);
    setFilteredVehicleData(rawVehicleData);
  };

  return (
    <div className="w-full bg-white">
      <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
        {/* Company Dropdown */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Company
          </label>
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            disabled={companyLoading}
          >
            <option value="">All Companies</option>
            {companyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Vehicle Type */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Vehicle Type
          </label>
          <select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            <option value="">Select Vehicle Type</option>
            {vehicleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            Vehicle Name
          </label>
          <select
            value={vehicleName}
            onChange={(e) => setVehicleName(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
          >
            <option value="">Select Vehicle Name</option>
            {vehicleNameOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="flex items-center gap-4">
            {/* Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="active"
                  checked={activeStatus}
                  onChange={(e) => setActiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setActiveStatus(!activeStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    activeStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      activeStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="active"
                className="text-sm text-gray-700 cursor-pointer"
              >
                Active
              </label>
            </div>

            {/* In-Active Toggle */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="checkbox"
                  id="inactive"
                  checked={inactiveStatus}
                  onChange={(e) => setInactiveStatus(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setInactiveStatus(!inactiveStatus)}
                  className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
                    inactiveStatus ? "bg-red-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
                      inactiveStatus ? "translate-x-6" : "translate-x-0.5"
                    } translate-y-0.5`}
                  />
                </div>
              </div>
              <label
                htmlFor="inactive"
                className="text-sm text-gray-700 cursor-pointer"
              >
                In-Active
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          <Button
            size="sm"
            onClick={handleSearch}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Search
          </Button>

          <Button
            size="sm"
            onClick={handleClear}
            icon={<Search className="w-4 h-4" />}
            iconPosition="left"
          >
            Clear
          </Button>
        </div>
      </div>
      <div className="mt-10">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Vehicle Name
          </Button>

          <Button
            size="sm"
            onClick={() => onStatusChanged(1)}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Publish
          </Button>

          {/* Unpublish Button with Download Icon */}
          <Button
            size="sm"
            onClick={() => onStatusChanged(0)}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Unpublish
          </Button>

          {/* Delete Button with Trash Icon (Danger Variant) */}
          <Button
            size="sm"
            variant="danger"
            onClick={() => onStatusChanged(2)}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            Delete
          </Button>
        </div>
        <BookingTable
          data={vechicleData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={false}
          sortable={true}
          itemsPerPage={5}
        />
      </div>

      {/* Update Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Update Vehicle Name
                </h2>
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields}
                  onSubmit={(formData: Record<string, any>) =>
                    handleFormSubmit(formData, "update")
                  }
                  submitText="Update Vehicle Type"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsAddModalOpen(false);
            }}
          />
          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  Add New Vehicle Name
                </h2>
                <button
                  onClick={() => {
                    setIsAddModalOpen(false);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>
              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <DynamicForm
                  fields={formFields.map((field: any) => ({
                    ...field,
                    defaultValue: field.id === "status" ? "1" : "",
                  }))}
                  onSubmit={(formData: Record<string, any>) =>
                    handleFormSubmit(formData, "add")
                  }
                  submitText="Add Vehicle Name"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageVehicleName;


// "use client";

// import React, { useState, useEffect } from "react";
// import BookingTable from "../common/Table";

// import {
//   useUpdateVehicleNameStatus,
//   useAddVehicleName,
//   getVehicleNameTypeList,
//   getVehicleTypeList,
//   getVehicleAmenitiesList,
//   useUpdateVehicleNameData,
// } from "@/hooks/useCommon";

// import {
//   X,
//   Trash2,
//   Edit,
//   UploadIcon,
//   DownloadCloudIcon,
//   TrashIcon,
//   Plus,
//   Search,
// } from "lucide-react";
// import DynamicForm from "./Popup";
// import Button from "./Button";
// import { toast } from "react-toastify";
// import { useAuth } from "@/store/auth";


// interface TableData {
//   vehicle_type_id: number;
//   sno: number;
//   order: string;
//   image: string;
//   vehicleType: string;
//   seatingCapacity: string;
//   luggage: string;
//   luggage_capacity: string;
//   small_suitcase: string;
//   person_capacity: string;
//   no_of_doors: number;
//   no_of_airbags: number;
//   vehicleImage: string;
//   leftVehicleImage: string;
//   rightVehicleImage: string;
//   frontVehicleImage: string;
//   backVehicleImage: string;
//   interiorVehicleImage: string;
//   createdBy: string;
//   status: string;
//   name: string;
//   amenities_id: string;
//   amenties: string;
//   id: string;
// }

// const ManageVehicleName = () => {
//   const [vehicleType, setVehicleType] = useState("");
//   const [activeStatus, setActiveStatus] = useState(true);
//   const [inactiveStatus, setInactiveStatus] = useState(false);
//   const [currentRow, setCurrentRow] = useState<TableData | null>(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   const [uploadingImages, setUploadingImages] = useState<
//     Record<string, boolean>
//   >({});
//   const [uploadedImageUrls, setUploadedImageUrls] = useState<
//     Record<string, string>
//   >({});
//   const [isAddModalOpen, setIsAddModalOpen] = useState(false);
//   const { data: vehicleDetail, refetch } = getVehicleNameTypeList();
//   const [rawVehicleData, setRawVehicleData] = useState<any[]>([]);
//   const [filteredVehicleData, setFilteredVehicleData] = useState<any[]>([]);
//   const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);
//   const { mutate: updateStatusMutate } = useUpdateVehicleNameStatus();
//   const { mutate: addVehicleTypeMutate } = useAddVehicleName();
//   const { mutate: updateVehicleTypeMutate } = useUpdateVehicleNameData();
//   const [vehicleName, setVehicleName] = useState("");
//   const { data: vehicleTypeList, refetch: vListRefech } = getVehicleTypeList();
//   const { data: vehicleAmenties, refetch: refetchAmenties } =
//     getVehicleAmenitiesList();

//   const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
//   const user = useAuth(); 
//   const userId = user?.user?.id;

//   useEffect(() => {
//     refetch();
//     vListRefech();
//     refetchAmenties();
//   }, []);
//   useEffect(() => {
//     if (Array.isArray(vehicleDetail)) {
//       setRawVehicleData(vehicleDetail);
//       setFilteredVehicleData(vehicleDetail);
//     }
//   }, [vehicleDetail]);

//   const vechicleData = Array.isArray(filteredVehicleData)
//     ? filteredVehicleData.map((item: any, index: number) => ({
//         sno: index + 1,
//         order: item.display_order,
//         amenities: item.amenities,
//         id: item.id,
//         vehicle_image: item.image,
//         leftVehicleImage: item.left_image,
//         rightVehicleImage: item.right_image,
//         frontVehicleImage: item.front_image,
//         backVehicleImage: item.back_image,
//         interiorVehicleImage: item.interior_image,
//         vehicleType: item.vehicle_type,
//         no_of_doors: item.no_of_doors,
//         no_of_airbags: item.no_of_airbags,
//         vehicle_type: item.vehicle_type_name,
//         status: item.status,
//         created_name: item.created_name,
//         name: item.name,
//         aircondition: item.aircondition,
//         email: item.email,
//         mobile: item.mobile,
//         luggage_capacity: item.luggage_capacity,
//         person_capacity: item.person_capacity,
//         small_suitcase: item.small_suitcase,
//         vehicle_type_id: item.vehicle_type_id,
//         vehicle_type_name: item.vehicle_type_name,
//         amenties: item.amenties,
//         action: "View",
//       }))
//     : [];

//   const handleSearch = () => {
//     const filtered = rawVehicleData.filter((item) => {
//       const matchVehicleType = vehicleType
//         ? item.vehicle_type_id == vehicleType
//         : true;

//       const matchStatus =
//         activeStatus && inactiveStatus
//           ? true
//           : activeStatus
//           ? item.status == 1
//           : inactiveStatus
//           ? item.status == 0
//           : true;
//       const matchName = vehicleName ? item.id == vehicleName : true;

//       return matchVehicleType && matchStatus && matchName;
//     });

//     setFilteredVehicleData(filtered);
//   };


//   const handleFormSubmit = (formData: Record<string, any>, type: string) => {
//   const {
//     vehicleType,
//     status,
//     vehicleImage,
//     vehicleName,
//     luggage,
//     seatingCapacity,
//     no_of_doors,
//     no_of_airbags,
//     smallSuitcase,
//     amenties,
//     leftVehicleImage,
//     rightVehicleImage,
//     frontVehicleImage,
//     backVehicleImage,
//     interiorVehicleImage,
//   } = formData;

//   const mainImageFile = vehicleImage instanceof FileList ? vehicleImage[0] : vehicleImage;
//   const leftImageFile = leftVehicleImage instanceof FileList ? leftVehicleImage[0] : leftVehicleImage;
//   const rightImageFile = rightVehicleImage instanceof FileList ? rightVehicleImage[0] : rightVehicleImage;
//   const frontImageFile = frontVehicleImage instanceof FileList ? frontVehicleImage[0] : frontVehicleImage;
//   const backImageFile = backVehicleImage instanceof FileList ? backVehicleImage[0] : backVehicleImage;
//   const interiorImageFile = interiorVehicleImage instanceof FileList ? interiorVehicleImage[0] : interiorVehicleImage;

//   const matchedVehicle = vehicleTypeList.find(
//     (item: any) => item.id == vehicleType
//   );

//   console.log("matched vehicle", matchedVehicle);

//   const val = matchedVehicle?.id ?? "";

  

//   const amenityString = Array.isArray(amenties)
//     ? amenties.map((item) => item.label).join(",")
//     : "";
//   const amenityIds = Array.isArray(amenties)
//     ? amenties.map((item) => item.value).join(",")
//     : "";

//   // Create payload with all images
//   const payload: any = {
//     file: mainImageFile,
//     folder: vehicleName,
//     category_id: vehicleType,
//     status,
//     vehicle_type_id: val,
//     name: vehicleName,
//     person_capacity: seatingCapacity,
//     luggage_capacity: luggage,
//     small_suitcase: smallSuitcase,
//     vehicle_type_name: matchedVehicle?.vehicle_type,
//     amenities: amenityString,
//     amenities_id: amenityIds,
//     no_of_doors: no_of_doors,
//     no_of_airbags: no_of_airbags,
//   };

//   console.log("Payload before ID addition:", payload);

//   // Add the ID for update operation
//   if (type === "update") {
//     payload.id = currentRow?.id;
//   }

//   // Add image files to payload
//   if (leftImageFile) {
//     payload.left = leftImageFile;
//   }
//   if (rightImageFile) {
//     payload.right = rightImageFile;
//   }
//   if (frontImageFile) {
//     payload.front = frontImageFile;
//   }
//   if (backImageFile) {
//     payload.back = backImageFile;
//   }
//   if (interiorImageFile) {
//     payload.interior = interiorImageFile;
//   }

//   if (type == "update") {
//     updateVehicleTypeMutate(
//       payload,
//       {
//         onSuccess: (res) => {
//           toast.success("Vehicle name updated successfully");
//           refetch();
//           setIsModalOpen(false);
//           setCurrentRow(null);
//         },
//         onError: () => {
//           toast.error("Failed to update vehicle name");
//         },
//       }
//     );
//   } else {
//     addVehicleTypeMutate(
//       payload,
//       {
//         onSuccess: (res) => {
//           toast.success("Vehicle name added successfully");
//           refetch();
//           setIsAddModalOpen(false);
//           setCurrentRow(null);
//         },
//         onError: () => {
//           toast.error("Failed to add vehicle name");
//         },
//       }
//     );
//   }
// };

//   // const handleFormSubmit = (formData: Record<string, any>, type: string) => {
//   //   const {
//   //     vehicleType,
//   //     status,
//   //     vehicleImage,
//   //     vehicleName,
//   //     luggage,
//   //     seatingCapacity,
//   //     smallSuitcase,
//   //     amenties,
//   //   } = formData;

//   //   const file =
//   //     vehicleImage instanceof FileList ? vehicleImage[0] : vehicleImage;

//   //   const matchedVehicle = vehicleTypeList.find(
//   //     (item: any) => item.id == vehicleType
//   //   );

//   //   const val = matchedVehicle?.vehicle_type ?? "";

//   //   const amenityString = Array.isArray(amenties)
//   //     ? amenties.map((item) => item.label).join(",")
//   //     : "";
//   //   const amenityIds = Array.isArray(amenties)
//   //     ? amenties.map((item) => item.value).join(",")
//   //     : "";
//   //   if (type == "update") {
//   //     updateVehicleTypeMutate(
//   //       {
//   //         file,
//   //         folder: vehicleName,
//   //         category_id: vehicleType,
//   //         status,
//   //         vehicle_type: val,
//   //         name: vehicleName,
//   //         person_capacity: seatingCapacity,
//   //         luggage_capacity: luggage,
//   //         small_suitcase: smallSuitcase,
//   //         vehicle_type_name: val,
//   //         amenities: amenityString,
//   //         amenities_id: amenityIds,
//   //         id: currentRow?.id,
//   //       },
//   //       {
//   //         onSuccess: (res) => {
//   //           toast.success("Vehicle name updated successfully");
//   //           refetch();
//   //           setIsModalOpen(false);
//   //           setCurrentRow(null);
//   //         },
//   //         onError: () => {
//   //           toast.error("Failed to update vehicle name");
//   //         },
//   //       }
//   //     );
//   //   } else {
//   //     addVehicleTypeMutate(
//   //       {
//   //         file,
//   //         folder: "vehicle-type-image",
//   //         category_id: vehicleType,
//   //         status,
//   //         vehicle_type: val,
//   //         name: vehicleName,
//   //         person_capacity: seatingCapacity,
//   //         luggage_capacity: luggage,
//   //         small_suitcase: smallSuitcase,
//   //         vehicle_type_name: val,
//   //         amenities: amenityString,
//   //         amenities_id: amenityIds,
//   //       },
//   //       {
//   //         onSuccess: (res) => {
//   //           toast.success("Vehicle name added successfully");
//   //           refetch();
//   //           setIsAddModalOpen(false);
//   //           setCurrentRow(null);
//   //         },
//   //         onError: () => {
//   //           toast.error("Failed to add vehicle name");
//   //         },
//   //       }
//   //     );
//   //   }
//   // };
//   const onStatusChanged = (status_id: number, id?: any) => {
//     let type_ids = [id];
//     if (!id) {
//       if (selectedRowIds.length === 0) {
//         toast.error("No rows selected");
//         return;
//       }

//       const selectedRoutes = filteredVehicleData.filter((row) =>
//         selectedRowIds.includes(row.id)
//       );
//       type_ids = selectedRoutes.map((row) => row.id);
//     }
//     updateStatusMutate(
//       {
//         status: status_id,
//         id: type_ids,
//       },
//       {
//         onSuccess: (data) => {
//           if (data.status == "success") {
//             status_id == 2 ? "Data deleted successully" : data?.message;
//           }
//           refetch();
//           setSelectedRowIds([]);
//         },
//         onError: (error) => {
//           toast.error("Something want wrong");
//         },
//       }
//     );
//   };

//   const handleSelectAll = (isChecked: boolean) => {
//     if (isChecked) {
//       const allIds = filteredVehicleData.map((row) => row.id);
//       setSelectedRowIds(allIds);
//     } else {
//       setSelectedRowIds([]);
//     }
//   };

//   const handleCheckboxChange = (id: number, isChecked: boolean) => {
//     setSelectedRowIds((prev) =>
//       isChecked ? [...prev, id] : prev.filter((item) => item !== id)
//     );
//   };

//   const allRowsSelected =
//     filteredVehicleData?.length > 0 &&
//     selectedRowIds?.length === filteredVehicleData?.length;

//   const getImageUrl = (url: any) => {
//     const rawFilePath = url || "";
//     const fileParts = rawFilePath.split("/");
//     const folderPath = fileParts.slice(0, -1).join("/");
//     const fileName = fileParts[fileParts.length - 1];
//     const imageUrl = `${profilePath}${folderPath}/${encodeURIComponent(
//       fileName
//     )}`;

   
//     return imageUrl;
//   };

//   const columns = [
//     {
//       key: "checkbox",
//       header: (
//         <input
//           type="checkbox"
//           onChange={(e) => handleSelectAll(e.target.checked)}
//           checked={allRowsSelected}
//         />
//       ),
//       sortable: false,
//       render: (_: any, row: any) => (
//         <input
//           type="checkbox"
//           checked={selectedRowIds.includes(row.id)}
//           onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
//         />
//       ),
//     },
//     { key: "sno", header: "S.No", sortable: true },
//     // {
//     //   key: "vehicle_image",
//     //   header: "Image",
//     //   sortable: true,
//     //   render: (_: any, row: any) => {
        
//     //     const imageUrl = getImageUrl(row?.vehicle_image);
//     //     return (
//     //       <img
//     //         src={imageUrl || "/no-preview.png"}
//     //         alt="Document Preview"
//     //         className="w-full h-48 object-contain rounded bg-white"
//     //       />
//     //     );
//     //   },
//     // },
//     {
//       key: "left_image",
//       header: "Left Image",
//       sortable: true,
//       render: (_: any, row: any) => {
//         const imageUrl = getImageUrl(row?.leftVehicleImage);
       
//         return (
//           <img
//             src={imageUrl || "/no-preview.png"}
//             alt="Document Preview"
//             className="w-24 h-20 object-contain rounded bg-white"
//           />
//         );
//       },
//     },
//     {
//       key: "right_image",
//       header: "Right Image",
//       sortable: true,
//       render: (_: any, row: any) => {
//         const imageUrl = getImageUrl(row?.rightVehicleImage);
//         return (
//           <img
//             src={imageUrl || "/no-preview.png"}
//             alt="Document Preview"
//             className="w-24 h-20 object-contain rounded bg-white"
//           />
//         );
//       },
//     },
//     {
//       key: "front_image",
//       header: "Front Image",
//       sortable: true,
//       render: (_: any, row: any) => {
//         const imageUrl = getImageUrl(row?.frontVehicleImage);
//         return (
//           <img
//             src={imageUrl || "/no-preview.png"}
//             alt="Document Preview"
//             className="w-24 h-20 object-contain rounded bg-white"
//           />
//         );
//       },
//     },
//     {
//       key: "back_image",
//       header: "Back Image",
//       sortable: true,
//       render: (_: any, row: any) => {
//         const imageUrl = getImageUrl(row?.backVehicleImage);
//         return (
//           <img
//             src={imageUrl || "/no-preview.png"}
//             alt="Document Preview"
//             className="w-24 h-20 object-contain rounded bg-white"
//           />
//         );
//       },
//     },
//     {
//       key: "interior_image",
//       header: "Interior Image",
//       sortable: true,
//       render: (_: any, row: any) => {
//         const imageUrl = getImageUrl(row?.interiorVehicleImage);
//         return (
//           <img
//             src={imageUrl || "/no-preview.png"}
//             alt="Document Preview"
//             className="w-24 h-20 object-contain rounded bg-white"
//           />
//         );
//       },
//     },
//     { key: "name", header: "Vehicle Name", sortable: true },
//     { key: "vehicle_type", header: "Vehicle Type", sortable: true },
//     { key: "amenties", header: "amenties", sortable: true },
//     { key: "id", header: "id", sortable: true, hidden: true },
//     { key: "person_capacity", header: "Seating Capacity", sortable: true },
//     { key: "luggage_capacity", header: "Luggae Capacity", sortable: true },
//     { key: "small_suitcase", header: "Small Suitcase", sortable: true },
//     { key: "no_of_airbags", header: "No. Of Airbags", sortable: true },
//     { key: "no_of_doors", header: "No. Of Doors", sortable: true },
//     {
//       key: "created_name",
//       header: "Created By",
//       sortable: true,
//       render: (_: string, row: any) => (
//         <div className="space-y-1">
//           <div> {row?.created_name}</div>
//           <div> {row?.mobile}</div>
//           <div> {row?.email}</div>
//         </div>
//       ),
//     },
//     {
//       key: "status",
//       header: "Status",
//       sortable: true,
//       render: (value: number | string) => {
//         const isOnline = Number(value) == 1;
//         return (
//           <div className="flex items-center gap-2">
//             <span
//               className={`h-2.5 w-2.5 rounded-full ${
//                 isOnline ? "bg-green-500" : "bg-red-500"
//               }`}
//             />
//             <span className="text-sm font-medium">
//               {/* {currentStatus?.label || "Unknown"} */}
//             </span>
//           </div>
//         );
//       },
//     },
//     {
//       key: "action",
//       header: "Action",
//       render: (_: any, row: any) => {
//         return (
//           <div className="flex space-x-2">
//             <button
//               className="text-blue-500 hover:text-blue-700"
//               onClick={() => {
//                 setCurrentRow(row);
//                 setIsModalOpen(true);
//               }}
//             >
//               <Edit size={15} />
//             </button>
//             <button
//               className="text-red-500 hover:text-red-700"
//               onClick={() => onStatusChanged(2)}
//             >
//               <Trash2 size={15} />
//             </button>
//           </div>
//         );
//       },
//     },
//   ];
//   const vehicleOptions = Array.isArray(vehicleTypeList)
//     ? vehicleTypeList.map((item: any) => ({
//         label: item.vehicle_type,
//         value: item.id,
//       }))
//     : [];

//   const amentiesListOptions = Array.isArray(vehicleAmenties)
//     ? vehicleAmenties.map((item: any) => ({
//         label: item.amenities_name,
//         value: item.id,
//       }))
//     : [];

//   const vehicleNameOptions = Array.isArray(vehicleDetail)
//     ? vehicleDetail.map((item: any) => ({
//         label: item.name,
//         value: item.id,
//       }))
//     : [];

//   const formFields: any = [
//     {
//       id: "vehicleType",
//       label: "Vehicle Type",
//       type: "select",
//       placeholder: "Select Vehicle Type",
//       required: true,
//       defaultValue: currentRow?.vehicle_type_id || "",
//       options: vehicleOptions,
//     },
//     {
//       id: "vehicleName",
//       label: "Vehicle Name",
//       type: "text",
//       placeholder: "Enter Vehicle Name",
//       required: true,
//       defaultValue: currentRow?.name || "",
//     },
//     {
//       id: "amenties",
//       label: "Amenities Type",
//       type: "multiselect", // ✅ custom type
//       placeholder: "Select Amenities",
//       required: true,
//       defaultValue:
//         typeof currentRow?.amenties === "string"
//           ? currentRow.amenties.split(",").map((label: string) => {
//               const trimmed = label.trim();
//               return {
//                 label: trimmed,
//                 value: trimmed,
//               };
//             })
//           : currentRow?.amenties || [],
//       options: amentiesListOptions,
//     },
//     {
//       id: "seatingCapacity",
//       label: "Seating Capacity",
//       type: "number",
//       placeholder: "Enter Seating Capacity",
//       required: true,
//       defaultValue: currentRow?.person_capacity || "",
//     },

//     {
//       id: "luggage",
//       label: "Luggage Capacity",
//       type: "number",
//       placeholder: "Enter Luggage Capacity",
//       required: true,
//       defaultValue: currentRow?.luggage_capacity.toString() || "",
//     },
//     {
//       id: "smallSuitcase",
//       label: "Small Suitcase",
//       type: "number",
//       placeholder: "Enter Small Suitcase Capacity",
//       required: true,
//       defaultValue: currentRow?.small_suitcase || "",
//       options: vehicleOptions,
//     },

//     {
//       id: "no_of_doors",
//       label: "No. Of Doors",
//       type: "number",
//       placeholder: "Enter No. of Doors",
//       required: true,
//       defaultValue: currentRow?.no_of_doors || "",
//     },
//      {
//       id: "no_of_airbags",
//       label: "No. Of Airbags",
//       type: "number",
//       placeholder: "Enter No. of Airbags",
    
//       defaultValue: currentRow?.no_of_airbags || "",
//     },
  

//     {
//       id: "leftVehicleImage",
//       label: "Leftside Vehicle Image",
//       type: "file",
//       placeholder: "Upload Vehicle Image",
//       required: true,
//       defaultValue: currentRow?.leftVehicleImage || "",
//       previewUrl: currentRow?.leftVehicleImage ? getImageUrl(currentRow.leftVehicleImage) : "",
//     },
//     {
//       id: "rightVehicleImage",
//       label: "Rightside Vehicle Image",
//       type: "file",
//       placeholder: "Upload Vehicle Image",
//       required: true,
//       defaultValue: currentRow?.rightVehicleImage || "",
//       previewUrl: currentRow?.rightVehicleImage ? getImageUrl(currentRow.rightVehicleImage) : "",
//     },
//     {
//       id: "frontVehicleImage",
//       label: "FrontSide Vehicle Image",
//       type: "file",
//       placeholder: "Upload front Vehicle Image",
//       required: true,
//       defaultValue: currentRow?.frontVehicleImage || "",
//       previewUrl: currentRow?.frontVehicleImage ? getImageUrl(currentRow.frontVehicleImage) : "",
//     },
//     {
//       id: "backVehicleImage",
//       label: "Back Vehicle Image",
//       type: "file",
//       placeholder: "Upload Back Vehicle Image",
//       required: true,
//       defaultValue: currentRow?.backVehicleImage || "",
//        previewUrl: currentRow?.backVehicleImage ? getImageUrl(currentRow.backVehicleImage) : "",
//     },
//     {
//       id: "interiorVehicleImage",
//       label: "Interior Vehicle Image",
//       type: "file",
//       placeholder: "Upload Interior Vehicle Image",
//       required: true,
//       defaultValue: currentRow?.interiorVehicleImage || "",
//       previewUrl: currentRow?.interiorVehicleImage ? getImageUrl(currentRow.interiorVehicleImage) : "",
//     },
//     {
//       id: "status",
//       label: "Status",
//       type: "select",
//       placeholder: "Select Status",
//       required: true,
//       defaultValue: currentRow?.status.toString() || "Active",
//       options: [
//         { value: "1", label: "Active" },
//         { value: "0", label: "In-Active" },
//       ],
//     },
//   ];

//   const handleClear = () => {
//     setVehicleType("");
//     setVehicleName("");
//     setActiveStatus(true);
//     setInactiveStatus(false);
//     setFilteredVehicleData(rawVehicleData);
//   };

//   return (
//     <div className="w-full bg-white">
//       <div className="flex flex-wrap items-center gap-6 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
//         {/* Vehicle Type */}
//         <div className="flex flex-col gap-2">
//           <label className="text-sm font-medium text-gray-700">
//             Vehicle Type
//           </label>
//           <select
//             value={vehicleType}
//             onChange={(e) => setVehicleType(e.target.value)}
//             className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
//           >
//             <option value="">Select Vehicle Type</option>
//             {vehicleOptions.map((option) => (
//               <option key={option.value} value={option.value}>
//                 {option.label}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="flex flex-col gap-2">
//           <label className="text-sm font-medium text-gray-700">
//             Vehicle Name
//           </label>
//           <select
//             value={vehicleName}
//             onChange={(e) => setVehicleName(e.target.value)}
//             className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
//           >
//             <option value="">Select Vehicle Name</option>
//             {vehicleNameOptions.map((option) => (
//               <option key={option.value} value={option.value}>
//                 {option.label}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Status */}
//         <div className="flex flex-col gap-2">
//           <label className="text-sm font-medium text-gray-700">Status</label>
//           <div className="flex items-center gap-4">
//             {/* Active Toggle */}
//             <div className="flex items-center gap-2">
//               <div className="relative">
//                 <input
//                   type="checkbox"
//                   id="active"
//                   checked={activeStatus}
//                   onChange={(e) => setActiveStatus(e.target.checked)}
//                   className="sr-only"
//                 />
//                 <div
//                   onClick={() => setActiveStatus(!activeStatus)}
//                   className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
//                     activeStatus ? "bg-red-500" : "bg-gray-300"
//                   }`}
//                 >
//                   <div
//                     className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
//                       activeStatus ? "translate-x-6" : "translate-x-0.5"
//                     } translate-y-0.5`}
//                   />
//                 </div>
//               </div>
//               <label
//                 htmlFor="active"
//                 className="text-sm text-gray-700 cursor-pointer"
//               >
//                 Active
//               </label>
//             </div>

//             {/* In-Active Toggle */}
//             <div className="flex items-center gap-2">
//               <div className="relative">
//                 <input
//                   type="checkbox"
//                   id="inactive"
//                   checked={inactiveStatus}
//                   onChange={(e) => setInactiveStatus(e.target.checked)}
//                   className="sr-only"
//                 />
//                 <div
//                   onClick={() => setInactiveStatus(!inactiveStatus)}
//                   className={`w-12 h-6 rounded-full cursor-pointer transition-colors duration-200 ${
//                     inactiveStatus ? "bg-red-500" : "bg-gray-300"
//                   }`}
//                 >
//                   <div
//                     className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-200 ${
//                       inactiveStatus ? "translate-x-6" : "translate-x-0.5"
//                     } translate-y-0.5`}
//                   />
//                 </div>
//               </div>
//               <label
//                 htmlFor="inactive"
//                 className="text-sm text-gray-700 cursor-pointer"
//               >
//                 In-Active
//               </label>
//             </div>
//           </div>
//         </div>

//         {/* Action Buttons */}
//         <div className="flex gap-2 ml-auto">
//           <Button
//             size="sm"
//             onClick={handleSearch}
//             icon={<Search className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Search
//           </Button>

//           <Button
//             size="sm"
//             onClick={handleClear}
//             icon={<Search className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Clear
//           </Button>
//         </div>
//       </div>
//       <div className="mt-10">
//         <div className="flex justify-end gap-2">
//           <Button
//             size="sm"
//             onClick={() => setIsAddModalOpen(true)}
//             icon={<Plus className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Add Vehicle Name
//           </Button>

//           <Button
//             size="sm"
//             onClick={() => onStatusChanged(1)}
//             icon={<UploadIcon className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Publish
//           </Button>

//           {/* Unpublish Button with Download Icon */}
//           <Button
//             size="sm"
//             onClick={() => onStatusChanged(0)}
//             icon={<DownloadCloudIcon className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Unpublish
//           </Button>

//           {/* Delete Button with Trash Icon (Danger Variant) */}
//           <Button
//             size="sm"
//             variant="danger"
//             onClick={() => onStatusChanged(2)}
//             icon={<TrashIcon className="w-4 h-4" />}
//             iconPosition="left"
//           >
//             Delete
//           </Button>
//         </div>
//         <BookingTable
//           data={vechicleData}
//           columns={columns}
//           heading={[]}
//           searchable={true}
//           filterable={false}
//           sortable={true}
//           itemsPerPage={5}
//         />
//       </div>

//       {/* Update Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 z-50 overflow-y-auto">
//           <div
//             className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
//             onClick={() => {
//               setIsModalOpen(false);
//             }}
//           />
//           <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
//             <div
//               className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
//                 <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
//                   Update Vehicle Name
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setIsModalOpen(false);
//                   }}
//                   className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
//                   aria-label="Close modal"
//                 >
//                   <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
//                 </button>
//               </div>
//               <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
//                 <DynamicForm
//                   fields={formFields}
//                   onSubmit={(formData: Record<string, any>) =>
//                     handleFormSubmit(formData, "update")
//                   }
//                   submitText="Update Vehicle Type"
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Add Modal */}
//       {isAddModalOpen && (
//         <div className="fixed inset-0 z-50 overflow-y-auto">
//           <div
//             className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
//             onClick={() => {
//               setIsAddModalOpen(false);
//             }}
//           />
//           <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
//             <div
//               className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
//                 <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
//                   Add New Vehicle Name
//                 </h2>
//                 <button
//                   onClick={() => {
//                     setIsAddModalOpen(false);
//                   }}
//                   className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
//                   aria-label="Close modal"
//                 >
//                   <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
//                 </button>
//               </div>
//               <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
//                 <DynamicForm
//                   fields={formFields.map((field: any) => ({
//                     ...field,
//                     defaultValue: "",
//                   }))}
//                   onSubmit={(formData: Record<string, any>) =>
//                     handleFormSubmit(formData, "add")
//                   }
//                   submitText="Add Vehicle Name"
//                 />
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default ManageVehicleName;
